import { z } from "zod";

/**
 * User Configuration Service Types
 *
 * Schemas and types for the User Configuration service layer
 * Based on Fawkes API /users/{userId}/configuration-values endpoints
 */

// ============================================================================
// CORE CONFIGURATION VALUE SCHEMAS
// ============================================================================

/**
 * ConfigurationValue schema
 * From swagger2.json - represents a user-specific configuration value
 */
export const ConfigurationValueSchema = z.object({
	key: z.string().nullable(),
	value: z.string().nullable(),
});

export type ConfigurationValue = z.infer<typeof ConfigurationValueSchema>;

/**
 * Array of ConfigurationValues
 */
export const ConfigurationValuesArraySchema = z.array(ConfigurationValueSchema);

export type ConfigurationValues = z.infer<
	typeof ConfigurationValuesArraySchema
>;

// ============================================================================
// TABLE SETTINGS SCHEMAS
// ============================================================================

/**
 * Table sorting state schema
 */
export const TableSortingStateSchema = z.object({
	id: z.string(),
	desc: z.boolean(),
});

export type TableSortingState = z.infer<typeof TableSortingStateSchema>;

/**
 * Table sorting array schema (TanStack Table uses array for multi-sort)
 */
export const TableSortingArraySchema = z.array(TableSortingStateSchema);

export type TableSortingArray = z.infer<typeof TableSortingArraySchema>;

/**
 * Column visibility state schema (record of column ID to boolean)
 */
export const ColumnVisibilityStateSchema = z.record(z.string(), z.boolean());

export type ColumnVisibilityState = z.infer<typeof ColumnVisibilityStateSchema>;

/**
 * Column order state schema (array of column IDs)
 */
export const ColumnOrderStateSchema = z.array(z.string());

export type ColumnOrderState = z.infer<typeof ColumnOrderStateSchema>;

// ============================================================================
// RESPONSE TYPES
// ============================================================================

/**
 * Response type for getConfigurationValues
 */
export type GetConfigurationValuesResponse = ConfigurationValue[];

// ============================================================================
// ERROR HANDLING
// ============================================================================

/**
 * Custom error class for User Configuration API operations
 */
export class UserConfigApiError extends Error {
	constructor(
		message: string,
		public error: unknown,
		public code?: string,
		public status?: number,
		public details?: unknown,
	) {
		super(message);
		this.name = "UserConfigApiError";
	}

	/**
	 * Create error from API response
	 */
	static fromResponse(response: Response, data?: unknown): UserConfigApiError {
		const message = `API Error: ${response.status} ${response.statusText}`;
		return new UserConfigApiError(
			message,
			undefined,
			response.statusText,
			response.status,
			data,
		);
	}
}

// ============================================================================
// TABLE SETTINGS KEYS
// ============================================================================

/**
 * Table settings key suffixes
 */
export const TABLE_SETTINGS_KEYS = {
	SORTING: "sorting",
	COLUMN_ORDER: "columnOrder",
	COLUMN_VISIBILITY: "columnVisibility",
	SEARCH_TERM: "searchTerm",
	FILTERS: "filters",
	PAGE_SIZE: "pageSize",
} as const;

export type TableSettingsKeyType =
	(typeof TABLE_SETTINGS_KEYS)[keyof typeof TABLE_SETTINGS_KEYS];

/**
 * Helper to create table settings key
 */
export function createTableSettingsKey(
	tableKey: string,
	settingType: TableSettingsKeyType,
): string {
	return `${tableKey}.${settingType}`;
}

/**
 * Helper to parse table settings key
 */
export function parseTableSettingsKey(key: string): {
	tableKey: string;
	settingType: string;
} | null {
	const lastDotIndex = key.lastIndexOf(".");
	if (lastDotIndex === -1) return null;

	return {
		tableKey: key.substring(0, lastDotIndex),
		settingType: key.substring(lastDotIndex + 1),
	};
}
